import React from 'react';
import { FormattedMessage } from 'react-intl';

import { Checkbox } from 'ui-components/components/checkbox';
import { FlexColumn, Gap } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';

import { FbsApplicationDrawerSection } from '../fbs-application-drawer-section';
import styles from './fulfillment-capabilities-section.module.less';
import { messages } from './messages';

const textAreaId = 'fulfillment-capabilities-more-info-text-area';

export const fulfillmentCapabilityOptions = [
  { id: 'specialKittingBundling', labelMessageDescriptor: messages.specialKittingBundling },
  { id: 'serialNumberSupport', labelMessageDescriptor: messages.serialNumberSupport },
  { id: 'hazardousGoods', labelMessageDescriptor: messages.hazardousGoods },
  { id: 'coldStorage', labelMessageDescriptor: messages.coldStorage },
  { id: 'lotsExpirationDates', labelMessageDescriptor: messages.lotsExpirationDates },
  { id: 'heavyItems', labelMessageDescriptor: messages.heavyItems },
  { id: 'fragileItems', labelMessageDescriptor: messages.fragileItems },
  { id: 'largeItems', labelMessageDescriptor: messages.largeItems },
];

const CapabilityCheckbox = ({ id, checked, labelMessageDescriptor, onChange, disabled }) => {
  return (
    <Checkbox
      checked={checked}
      className={styles['capability-checkbox']}
      disabled={disabled}
      innerClassname={styles['capability-checkbox-label']}
      onChange={(_checked) => onChange(id, _checked)}
    >
      <FormattedMessage {...labelMessageDescriptor} />
    </Checkbox>
  );
};

/**
 * @param {object} props
 * @param {object} props.fulfillmentCapabilities
 * @param {(capability: string, checked: boolean) => void} props.onCapabilityChange
 * @param {boolean} props.isComplete
 * @param {boolean} props.isEditing
 * @param {boolean} props.isDisabled
 * @param {boolean} props.isContinueDisabled
 * @param {() => void} props.onContinue
 * @param {() => void} props.onEdit
 * @returns {React.JSX.Element}
 */
export const FulfillmentCapabilitiesSection = ({
  fulfillmentCapabilities,
  onCapabilityChange,
  isComplete,
  isEditing,
  isDisabled,
  isContinueDisabled,
  onContinue,
  onEdit,
}) => {
  const shouldShowMoreDetails = Object.values(fulfillmentCapabilities).some((x) => x);

  return (
    <FbsApplicationDrawerSection
      headerMessageDescriptor={messages.fulfillmentCapabilitiesTitle}
      isComplete={isComplete}
      isContinueDisabled={isContinueDisabled}
      isDisabled={isDisabled}
      isEditing={isEditing}
      onContinue={onContinue}
      onEdit={onEdit}
    >
      <FlexColumn gap={Gap.Space24}>
        <FlexColumn gap={Gap.Space16}>
          <div className={styles['fulfillment-capabilities-question']}>
            <Text textType={TextType.H4} value={messages.fulfillmentCapabilitiesDescription} />
            <Text
              className={styles['fulfillment-capabilities-caption']}
              element='span'
              textType={TextType.Caption}
              value={messages.selectAllThatApply}
            />
          </div>
          <div className={styles['capabilities-grid']}>
            {fulfillmentCapabilityOptions.map((option) => (
              <CapabilityCheckbox
                key={option.id}
                checked={fulfillmentCapabilities[option.id]}
                disabled={isComplete ? !isEditing : null}
                id={option.id}
                labelMessageDescriptor={option.labelMessageDescriptor}
                onChange={onCapabilityChange}
              />
            ))}
          </div>
        </FlexColumn>
        {shouldShowMoreDetails ? (
          <FlexColumn gap={Gap.Space16}>
            <Text element='label' htmlFor={textAreaId} textType={TextType.H4} value={messages.moreDetails} />
            {isComplete && !isEditing ? (
              <Text textType={TextType.Description} value={fulfillmentCapabilities.moreDetails} />
            ) : (
              <textarea
                className={styles['additional-info-textarea']}
                disabled={isComplete ? !isEditing : null}
                id={textAreaId}
                onChange={(event) => onCapabilityChange('moreDetails', event.target.value)}
                rows={4}
                value={fulfillmentCapabilities.moreDetails}
              />
            )}
          </FlexColumn>
        ) : null}
      </FlexColumn>
    </FbsApplicationDrawerSection>
  );
};
